import {gql} from '@apollo/client';
import {isPositiveNumber} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';

import {assert} from 'Asserts';
import {MixtureMeasurementUnit} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  MixtureGroupParentMixtureQuery,
  MixtureGroupParentMixtureQueryVariables,
} from 'notebook/integrations/registry/__generated/MixtureGroupParentMixtureQuery';
import {IngredientDataFromParent, NumericString} from 'notebook/integrations/registry/MixtureGroupConstants';
import {MixtureIngredientsStructuredTable} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {
  getMixtureAmountFromTable,
  getParentMixtureIdFromTable,
  MixtureMetadataStructuredTable,
} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {MixtureGroup} from 'notebook/integrations/registry/MixtureNoteLinkedGroupAdapter';
import {
  getUpdatedTableWithIngredientRowsFromParent,
  getUpdatedTableWithoutIngredientRowsFromParent,
  getUpdatedTableWithUnitsFromParent,
} from 'notebook/integrations/registry/MixtureTableUpdaters';
import {
  findNotePartForStructuredTable,
  reduceGroupUpdateNotePartItem,
} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {isMixtureMetadataStructuredTable} from 'notebook/TypePredicates';
import {NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import {MaybePromise} from 'util/Promises';

type MixtureGroupParentIngredient = ExtractByTypename<
  MixtureGroupParentMixtureQuery['mixture'],
  'MixtureNode'
>['ingredients'][number];

export const MIXTURE_GROUP_PARENT_MIXTURE_QUERY: DocumentNode = gql`
  query MixtureGroupParentMixtureQuery($mixtureId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    mixture(id: $mixtureId) {
      ... on MixtureNode {
        ...MixtureSpecificAttributesGraphQL
        ingredients {
          ...IngredientGraphQL
          componentBioentity {
            id
            displayLabel
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('MixtureSpecificAttributes').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function shouldChildIngredientBeInherited(parentIngredient: MixtureGroupParentIngredient): boolean {
  // Keep in sync with benchling/registry/mixtures/models/ingredient.py:_maybe_inherit_from_parent
  return (
    Boolean(parentIngredient.amount) &&
    Boolean(parentIngredient.componentBioentity) &&
    Boolean(parentIngredient.units)
  );
}

export function getIngredientsToCopyFromParent(
  parentIngredients: Array<MixtureGroupParentIngredient>
): Array<IngredientDataFromParent> {
  return parentIngredients.map((parentIngredient) => {
    const {componentBioentity, units, catalogIdentifier, id, amount, notes} = parentIngredient;
    return {
      componentBioentitySummary: {id: componentBioentity.id, displayLabel: componentBioentity.displayLabel},
      units,
      catalogIdentifier,
      notes,
      parentIngredientId: id,
      parentIngredientAmount: shouldChildIngredientBeInherited(parentIngredient)
        ? {stringValue: amount!, numericValue: Number(amount)}
        : null,
    };
  });
}

/**
 * This reducer is responsible for updating 2 pieces of information in the ingredient table note part:
 *   - Add rows to the table for ingredients being copied/inherited from the parent
 *   - Set the table's metdata.parentMixtureAmount value based on the new parent's amount
 */
function reduceGroupUpdateIngredientTableForNewParent(
  noteLinkedGroup: MixtureGroup,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>,
  ingredientsFromParent: Array<IngredientDataFromParent>,
  amountFromParent: string
): MixtureGroup {
  if (ingredientsFromParent.length === 0) {
    // metadata.parentMixtureAmount is only used to compute the target amount for inherited ingredients, so
    // no need to set it if there are no incoming ingredients from the parent
    return noteLinkedGroup;
  }
  const currentIngredientTable = ingredientTableNotePart.item;
  const tableWithUpdatedRows = getUpdatedTableWithIngredientRowsFromParent(
    currentIngredientTable,
    ingredientsFromParent
  );
  const updatedIngredientTable = {
    ...tableWithUpdatedRows,
    metadata: {
      ...tableWithUpdatedRows.metadata,
      parentMixtureAmount: {
        stringValue: amountFromParent,
        numericValue: Number(amountFromParent),
      },
    },
  };
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, ingredientTableNotePart, updatedIngredientTable);
}

function reduceGroupUpdateMetadataTableForNewParent(
  noteLinkedGroup: MixtureGroup,
  metadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  unitsFromParent: MixtureMeasurementUnit
): MixtureGroup {
  const currentMetadataTable = metadataTableNotePart.item;
  const updatedMetadataTable = getUpdatedTableWithUnitsFromParent(currentMetadataTable, unitsFromParent);
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, metadataTableNotePart, updatedMetadataTable);
}

function reduceGroupUpdateIngredientTableForRemovedParent(
  noteLinkedGroup: MixtureGroup,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>
): MixtureGroup {
  const currentIngredientTable = ingredientTableNotePart.item;
  const tableWithUpdatedRows = getUpdatedTableWithoutIngredientRowsFromParent(currentIngredientTable);
  const updatedIngredientTable = {
    ...tableWithUpdatedRows,
    metadata: {
      ...tableWithUpdatedRows.metadata,
      parentMixtureAmount: null,
    },
  };
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, ingredientTableNotePart, updatedIngredientTable);
}

async function asyncReduceGroupHandleNewParentMixture(
  noteLinkedGroup: MixtureGroup,
  metadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>,
  newParentMixtureId: string
): Promise<MixtureGroup> {
  const data = await doQuerySafe<MixtureGroupParentMixtureQuery, MixtureGroupParentMixtureQueryVariables>(
    {query: MIXTURE_GROUP_PARENT_MIXTURE_QUERY},
    {variables: {mixtureId: newParentMixtureId}}
  );
  const parentMixture = data.mixture;
  assert(
    !isInaccessible(parentMixture),
    'Expected parent mixture selected by user in notebook to be readable'
  );
  const updatedForParentIngredients = reduceGroupUpdateIngredientTableForNewParent(
    noteLinkedGroup,
    ingredientTableNotePart,
    getIngredientsToCopyFromParent(parentMixture.ingredients),
    parentMixture.amount
  );
  return reduceGroupUpdateMetadataTableForNewParent(
    updatedForParentIngredients,
    metadataTableNotePart,
    parentMixture.units
  );
}

export function reduceGroupHandleParentMixtureChange(
  noteLinkedGroup: MixtureGroup,
  currentMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  updatedMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>
): MaybePromise<MixtureGroup> {
  assert(
    [currentMetadataTableNotePart, updatedMetadataTableNotePart].every(
      (notePart) => notePart.item && isMixtureMetadataStructuredTable(notePart.item)
    ),
    'Note parts provided to reduceGroupHandleParentMixtureChange must correspond to a mixture metadata structured table'
  );
  const currentMetadataTable = currentMetadataTableNotePart.item;
  const updatedMetadataTable = updatedMetadataTableNotePart.item;
  const currentParentMixtureId = getParentMixtureIdFromTable(currentMetadataTable);
  const updatedParentMixtureId = getParentMixtureIdFromTable(updatedMetadataTable);
  if (currentParentMixtureId === updatedParentMixtureId) {
    // If the parent mixture ID didn't change, then no update to the group is needed
    return noteLinkedGroup;
  }
  const ingredientTableNotePart = findNotePartForStructuredTable<MixtureIngredientsStructuredTable>(
    noteLinkedGroup,
    StructuredTableKind.MIXTURE_INGREDIENTS
  );
  assert(
    ingredientTableNotePart != null,
    'Expected mixture group to contain a NotePart corresponding to the mixture ingredients structured table'
  );
  if (updatedParentMixtureId == null) {
    return reduceGroupUpdateIngredientTableForRemovedParent(noteLinkedGroup, ingredientTableNotePart);
  } else {
    return asyncReduceGroupHandleNewParentMixture(
      noteLinkedGroup,
      updatedMetadataTableNotePart,
      ingredientTableNotePart,
      updatedParentMixtureId
    );
  }
}

function reduceGroupUpdateIngredientTableForNewMixturePrepAmount(
  noteLinkedGroup: MixtureGroup,
  ingredientTableNotePart: NotePartWithItem<MixtureIngredientsStructuredTable>,
  updatedMixturePrepAmount: NumericString
): MixtureGroup {
  const currentIngredientTable = ingredientTableNotePart.item;
  const updatedIngredientTable = {
    ...currentIngredientTable,
    metadata: {
      ...currentIngredientTable.metadata,
      mixtureAmount: updatedMixturePrepAmount,
    },
  };
  return reduceGroupUpdateNotePartItem(noteLinkedGroup, ingredientTableNotePart, updatedIngredientTable);
}

export function reduceGroupHandleUpdatedMixturePrepAmount(
  noteLinkedGroup: MixtureGroup,
  currentMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>,
  updatedMetadataTableNotePart: NotePartWithItem<MixtureMetadataStructuredTable>
): MixtureGroup {
  assert(
    [currentMetadataTableNotePart, updatedMetadataTableNotePart].every(
      (notePart) => notePart.item && isMixtureMetadataStructuredTable(notePart.item)
    ),
    'Note parts provided to reduceGroupHandleUpdatedMixturePrepAmount must correspond to a mixture metadata structured table'
  );
  const currentMetadataTable = currentMetadataTableNotePart.item;
  const updatedMetadataTable = updatedMetadataTableNotePart.item;
  const currentAmount = getMixtureAmountFromTable(currentMetadataTable);
  const updatedAmount = getMixtureAmountFromTable(updatedMetadataTable);

  if (isPositiveNumber(updatedAmount) && updatedAmount !== currentAmount) {
    // Only update the group when the amount has changed and the new value represents a valid number
    const ingredientTableNotePart = findNotePartForStructuredTable<MixtureIngredientsStructuredTable>(
      noteLinkedGroup,
      StructuredTableKind.MIXTURE_INGREDIENTS
    );
    assert(
      ingredientTableNotePart != null,
      'Expected mixture group to contain a NotePart corresponding to the mixture ingredients structured table'
    );
    return reduceGroupUpdateIngredientTableForNewMixturePrepAmount(noteLinkedGroup, ingredientTableNotePart, {
      stringValue: updatedAmount,
      numericValue: Number(updatedAmount),
    });
  }
  return noteLinkedGroup;
}
